import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { firstValueFrom, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import * as XLSX from 'xlsx';
import { TranslateService } from '@ngx-translate/core';
import { GestionPlantillasService } from '../../gestion-plantillas/gestion-plantillas.service';
import { GestionUsuariosService } from '../../gestion-usuarios/gestion-usuarios.service';
import { Usuario } from '../../gestion-usuarios/usuario.model';
import {
  ComunicacionDetalle,
  ComunicacionPayload,
  DestinatarioMin,
  DomicilioPostal,
  DocumentoAdjunto,
  DocumentoRef,
  ProcedimientoRef,
  TipoEnvio,
  TIPO_ENVIO_COMUNICACION_ID,
  TIPO_ENVIO_NOTIFICACION_ID,
  ModoEnvioRemesa,
  modoEnvioFromRemesa,
  domicilioPostalVacio,
  etiquetaDestinatario,
  inicialesDestinatario
} from '../comunicacion.model';
import { GestionRemesasService } from '../gestion-remesas.service';

export interface RemesaFormData {
  comunicacion?: ComunicacionDetalle;
  tiposEnvio?: TipoEnvio[];
}

export type RemesaFormAction = 'draft' | 'send' | 'schedule' | 'request-sign';

export interface RemesaFormResult {
  action: RemesaFormAction;
  payload: ComunicacionPayload;
  fechaProgramada?: string;
  firmarAntesEnviar?: boolean;
  firmanteId?: number;
}

@Component({
  selector: 'app-remesa-form-drawer',
  templateUrl: './remesa-form-drawer.component.html',
  styleUrls: ['./remesa-form-drawer.component.scss']
})
export class RemesaFormDrawerComponent implements OnInit, OnDestroy {

  step = 1;
  form: FormGroup;
  selectedUserIds = new Set<number>();
  /** Cache of users we've seen, keyed by id. Persists across search changes. */
  selectedUsersCache = new Map<number, Usuario>();
  /** Destinatarios del Excel que no existen como usuarios (clave = NIF normalizado). */
  selectedExternos = new Map<string, DestinatarioMin>();
  displayedUsers: Usuario[] = [];
  procedimientos: ProcedimientoRef[] = [];
  searchUserQuery = '';
  searchLoading = false;
  isEdit = false;

  programar = false;
  fechaProgramada = '';
  firmarAntesEnviar = false;
  firmanteId: number | null = null;
  firmantes: Usuario[] = [];

  todayIso = new Date().toISOString().split('T')[0];

  readonly TIPO_COMUNICACION = TIPO_ENVIO_COMUNICACION_ID;
  readonly TIPO_NOTIFICACION = TIPO_ENVIO_NOTIFICACION_ID;

  // Excel import state
  importandoExcel = false;
  excelMensaje = '';
  excelNoEncontrados: string[] = [];
  excelIncompletos: string[] = [];
  permiteDestinatariosExternos = false;
  permitePostal = false;
  permitePostalInternacional = false;
  permiteEnvioSoloApp = true;
  permiteNotifica = true;
  algunMetodoEnvioActivo = true;
  modoEnvio: ModoEnvioRemesa = 'NOTIFICA';
  /** Domicilio postal de cada usuario de la aplicación (id → domicilio). */
  userDomicilios = new Map<number, DomicilioPostal>();
  /** Destinatario cuyo domicilio se está editando (uno cada vez). */
  editingUserId: number | null = null;
  editingExternoNif: string | null = null;

  // Documento de la notificación: 'generar' (plantilla + firma por destinatario)
  // | 'adjuntar' (un documento común subido por el admin).
  documentoMode: 'generar' | 'adjuntar' | 'ninguno' = 'generar';
  documentoAdjunto: DocumentoAdjunto | null = null;
  /** Adjunto ya persistido en la remesa (edición / copia); no requiere re-subida. */
  documentoExistente: DocumentoRef | null = null;
  documentoError = '';
  readonly documentoMaxBytes = 10 * 1024 * 1024; // 10 MB

  // Plantilla del documento (modo "generar"). null = plantilla por defecto.
  plantillas: { id: number; nombre: string }[] = [];
  plantillaId: number | null = null;

  /** Keystore de sello operativo para firmar PDFs generados desde plantilla. */
  firmaAutomaticaDisponible = true;
  firmaAutomaticaLoading = true;

  private searchSubject = new Subject<string>();
  private subs = new Subscription();

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<RemesaFormDrawerComponent>,
    @Inject(MAT_DIALOG_DATA) public data: RemesaFormData,
    private usuariosService: GestionUsuariosService,
    private remesasService: GestionRemesasService,
    private plantillasService: GestionPlantillasService,
    private translate: TranslateService
  ) {}

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }

  formTitleKey(): string {
    if (this.isEdit) {
      return this.esComunicacion() ? 'REMESAS.FORM.EDITAR_COMUNICACION' : 'REMESAS.FORM.EDITAR_NOTIFICACION';
    }
    return this.esComunicacion() ? 'REMESAS.FORM.NUEVA_COMUNICACION' : 'REMESAS.FORM.NUEVA_NOTIFICACION';
  }

  ngOnInit(): void {
    const c = this.data.comunicacion;
    this.isEdit = !!c;

    const tipoEnvioInicial = c?.tipoEnvio?.id ?? this.TIPO_COMUNICACION;
    const procedimientoInicial = c?.procedimiento?.sia ?? null;

    this.form = this.fb.group({
      tipoEnvio: [tipoEnvioInicial, Validators.required],
      concepto: [c?.concepto || '', [Validators.required, Validators.maxLength(200)]],
      descripcion: [c?.descripcion || '', Validators.maxLength(4000)],
      procedimiento: [procedimientoInicial]
    });

    // Pre-populate cache from existing destinatarios (edit mode)
    if (c?.destinatarios) {
      c.destinatarios.forEach(d => {
        if (d.externo || d.id == null) {
          const nif = this.normalizaNif(d.numero_documento);
          if (nif) {
            const extra = d.domicilio
              ? { ...d, externo: true, domicilio: { ...domicilioPostalVacio(), ...d.domicilio } }
              : { ...d, externo: true };
            this.selectedExternos.set(nif, extra);
          }
        } else {
          this.selectedUserIds.add(d.id);
          this.selectedUsersCache.set(d.id, this.destinatarioToUsuario(d));
          if (d.domicilio) {
            this.userDomicilios.set(d.id, { ...domicilioPostalVacio(), ...d.domicilio });
          }
        }
      });
    }

    // Server-side debounced search
    this.subs.add(
      this.searchSubject.pipe(
        debounceTime(350),
        distinctUntilChanged()
      ).subscribe(q => this.buscarUsuarios(q))
    );

    this.plantillaId = c?.plantilla?.id ?? null;
    this.documentoMode = this.resolveDocumentoMode(c);
    this.documentoExistente = c?.documento ?? null;
    this.firmarAntesEnviar = !!c?.firmarAntesEnviar;
    this.firmanteId = c?.firmadoPor?.id ?? null;
    this.modoEnvio = modoEnvioFromRemesa(c);

    this.cargaUsuarios();
    this.cargaProcedimientos();
    this.cargaPlantillas();
    this.cargaFirmaAutomatica();
    this.cargaFirmantes();
    this.cargaConfigRemesa();
  }

  get plantillaModoDisponible(): boolean {
    return this.firmaAutomaticaDisponible;
  }

  private resolveDocumentoMode(c?: ComunicacionDetalle): 'generar' | 'adjuntar' | 'ninguno' {
    if (c?.documentoModo === 'ADJUNTO' || (!c?.documentoModo && c?.documento)) return 'adjuntar';
    if (c?.documentoModo === 'NINGUNO') return 'ninguno';
    return 'generar';
  }

  get documentoAdjuntoLabel(): string {
    if (this.documentoAdjunto?.nombre) return this.documentoAdjunto.nombre;
    if (this.documentoExistente?.nombre) return this.documentoExistente.nombre;
    return '';
  }

  cargaFirmaAutomatica(): void {
    this.firmaAutomaticaLoading = true;
    this.remesasService.firmaAutomatica().subscribe({
      next: (status) => {
        this.firmaAutomaticaDisponible = !!status?.disponible;
        this.firmaAutomaticaLoading = false;
        if (!this.firmaAutomaticaDisponible && this.documentoMode === 'generar' && !this.isEdit) {
          this.setDocumentoMode('adjuntar');
        }
      },
      error: () => {
        this.firmaAutomaticaDisponible = false;
        this.firmaAutomaticaLoading = false;
        if (this.documentoMode === 'generar' && !this.isEdit) {
          this.setDocumentoMode('adjuntar');
        }
      }
    });
  }

  cargaFirmantes(): void {
    this.remesasService.firmantes().subscribe({
      next: (list) => { this.firmantes = (list || []) as Usuario[]; },
      error: () => { this.firmantes = []; }
    });
  }

  get esModoPostal(): boolean {
    return this.modoEnvio === 'NOTIFICA_POSTAL';
  }

  get esModoSoloApp(): boolean {
    return this.modoEnvio === 'APP';
  }

  cargaConfigRemesa(): void {
    this.remesasService.config().subscribe({
      next: (cfg) => {
        this.permiteDestinatariosExternos = !!cfg?.permiteDestinatariosExternos;
        this.permitePostal = !!cfg?.permitePostal;
        this.permitePostalInternacional = !!cfg?.permitePostalInternacional;
        this.permiteEnvioSoloApp = cfg?.permiteEnvioSoloApp !== false;
        this.permiteNotifica = cfg?.permiteNotifica !== false;
        this.algunMetodoEnvioActivo = cfg?.algunModoEnvioActivo !== false;
        if (!this.permiteDestinatariosExternos) {
          this.limpiarDestinatariosExternos();
        }
        this.ajustarModoEnvioSegunConfig();
        if (this.esModoPostal) {
          this.prefillDomiciliosUsuarios(Array.from(this.selectedUserIds));
          this.asegurarEdicionPostal();
        }
        if (this.esModoSoloApp) {
          this.limpiarDestinatariosExternos();
        }
      },
      error: () => {
        this.permiteDestinatariosExternos = false;
        this.permitePostal = false;
        this.permitePostalInternacional = false;
        this.permiteEnvioSoloApp = true;
        this.permiteNotifica = true;
        this.algunMetodoEnvioActivo = true;
        this.ajustarModoEnvioSegunConfig();
      }
    });
  }

  private modoEnvioPermitido(m: ModoEnvioRemesa): boolean {
    switch (m) {
      case 'APP':
        return this.permiteEnvioSoloApp;
      case 'NOTIFICA':
        return this.permiteNotifica;
      case 'NOTIFICA_POSTAL':
        return this.permitePostal;
    }
  }

  private ajustarModoEnvioSegunConfig(): void {
    if (this.modoEnvioPermitido(this.modoEnvio)) {
      return;
    }
    const candidatos: ModoEnvioRemesa[] = ['NOTIFICA', 'APP', 'NOTIFICA_POSTAL'];
    for (const m of candidatos) {
      if (this.modoEnvioPermitido(m)) {
        this.modoEnvio = m;
        return;
      }
    }
  }

  onModoEnvioChange(): void {
    if (this.esModoSoloApp) {
      this.limpiarDestinatariosExternos();
      this.editingUserId = null;
      this.editingExternoNif = null;
      return;
    }
    if (!this.permiteDestinatariosExternos) {
      this.limpiarDestinatariosExternos();
      this.editingExternoNif = null;
    }
    if (this.esModoPostal) {
      if (!this.permitePostalInternacional) {
        this.userDomicilios.forEach(d => this.forzarNacional(d));
        this.selectedExternosList().forEach(e => {
          if (e.domicilio) this.forzarNacional(e.domicilio);
        });
      }
      this.prefillDomiciliosUsuarios(Array.from(this.selectedUserIds));
      this.asegurarEdicionPostal();
      return;
    }
    this.editingUserId = null;
    this.editingExternoNif = null;
  }

  private limpiarDestinatariosExternos(): void {
    this.selectedExternos.clear();
    this.editingExternoNif = null;
  }

  get plantillaNombreSel(): string {
    if (this.plantillaId == null) return this.t('REMESAS.FORM.PLANTILLA_DEFECTO');
    return this.plantillas.find(p => p.id === this.plantillaId)?.nombre || this.t('REMESAS.FORM.PLANTILLA_DEFECTO');
  }

  get adjuntoEsPdf(): boolean {
    const mime = (this.documentoAdjunto?.mime || this.documentoExistente?.mime || '').toLowerCase();
    const nombre = (this.documentoAdjunto?.nombre || this.documentoExistente?.nombre || '').toLowerCase();
    return mime.includes('pdf') || nombre.endsWith('.pdf');
  }

  get puedeFirmarAntesEnviar(): boolean {
    if (!this.esNotificacion()) return false;
    if (this.documentoMode === 'ninguno') return false;
    if (this.documentoMode === 'adjuntar') return this.adjuntoEsPdf;
    return true;
  }

  cargaPlantillas(): void {
    this.plantillasService.activas().subscribe({
      next: (list) => { this.plantillas = list || []; },
      error: () => {}
    });
  }

  modoEnvioLabelKey(): string {
    switch (this.modoEnvio) {
      case 'APP': return 'REMESAS.FORM.MODO_APP';
      case 'NOTIFICA_POSTAL': return 'REMESAS.FORM.MODO_NOTIFICA_POSTAL';
      default: return 'REMESAS.FORM.MODO_NOTIFICA';
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  cargaProcedimientos(): void {
    this.remesasService.procedimientos().subscribe({
      next: (list) => { this.procedimientos = list || []; },
      error: () => {}
    });
  }

  cargaUsuarios(): void {
    this.searchLoading = true;
    this.usuariosService.list({ page: 0, size: 100 }).subscribe({
      next: (resp) => {
        this.displayedUsers = resp.data || [];
        this.cacheDisplayed();
        this.searchLoading = false;
      },
      error: () => { this.searchLoading = false; }
    });
  }

  onSearchChange(q: string): void {
    this.searchUserQuery = q;
    this.searchSubject.next(q);
  }

  private buscarUsuarios(query: string): void {
    this.searchLoading = true;
    const params: any = { page: 0, size: 100 };
    if (query.trim()) params.search = query.trim();
    this.usuariosService.list(params).subscribe({
      next: (resp) => {
        this.displayedUsers = resp.data || [];
        this.cacheDisplayed();
        this.searchLoading = false;
      },
      error: () => { this.searchLoading = false; }
    });
  }

  private cacheDisplayed(): void {
    this.displayedUsers.forEach(u => {
      if (this.selectedUserIds.has(u.id)) this.selectedUsersCache.set(u.id, u);
    });
  }

  // ---------- Navegación de pasos ----------
  next(): void {
    if (this.step === 1 && !this.step1Valid()) return;
    if (this.step === 2 && this.destinatariosCount() === 0) return;
    if (this.step === 2 && !this.destinatariosCompletos()) return;
    if (this.step < 3) this.step++;
  }

  prev(): void { if (this.step > 1) this.step--; }

  step1Valid(): boolean {
    if (!this.form.valid) return false;
    if (!this.form.get('tipoEnvio')?.value) return false;
    if (!this.form.get('concepto')?.value) return false;
    if (this.esNotificacion() && !this.form.get('procedimiento')?.value) return false;
    if (this.documentoMode === 'generar' && !this.firmaAutomaticaDisponible) return false;
    if (this.documentoMode === 'adjuntar' && !this.adjuntoValido()) return false;
    if (!this.algunMetodoEnvioActivo || !this.modoEnvioPermitido(this.modoEnvio)) return false;
    return true;
  }

  private adjuntoValido(): boolean {
    if (this.documentoAdjunto) return true;
    return !!this.documentoExistente?.idGestor;
  }

  procedimientoRequerido(): boolean {
    return this.esNotificacion();
  }

  // ---------- Tipología ----------
  selectTipoEnvio(value: number): void {
    this.form.get('tipoEnvio')?.setValue(value);
    if (!this.esNotificacion()) this.firmarAntesEnviar = false;
  }
  isTipoEnvio(value: number): boolean {
    return this.form.get('tipoEnvio')?.value === value;
  }
  esComunicacion(): boolean { return this.isTipoEnvio(this.TIPO_COMUNICACION); }
  esNotificacion(): boolean { return this.isTipoEnvio(this.TIPO_NOTIFICACION); }

  // ---------- Cuerpo ----------
  cuerpoLength(): number {
    return (this.form.get('descripcion')?.value || '').length;
  }

  // ---------- Destinatarios ----------
  filteredUsers(): Usuario[] {
    return this.displayedUsers;
  }

  toggleUser(u: Usuario): void {
    if (this.selectedUserIds.has(u.id)) {
      this.selectedUserIds.delete(u.id);
      this.userDomicilios.delete(u.id);
      this.asegurarEdicionPostal();
    } else {
      this.selectedUserIds.add(u.id);
      this.selectedUsersCache.set(u.id, u);
      const nif = this.normalizaNif(u.numero_documento);
      if (nif) this.selectedExternos.delete(nif);
      this.prefillDomiciliosUsuarios([u.id]);
      if (this.esModoPostal) this.editarUsuario(u.id);
    }
  }

  isUserSelected(u: Usuario): boolean { return this.selectedUserIds.has(u.id); }

  selectAllVisible(): void {
    const ids: number[] = [];
    this.displayedUsers.forEach(u => {
      this.selectedUserIds.add(u.id);
      this.selectedUsersCache.set(u.id, u);
      const nif = this.normalizaNif(u.numero_documento);
      if (nif) this.selectedExternos.delete(nif);
      ids.push(u.id);
    });
    this.prefillDomiciliosUsuarios(ids);
    this.asegurarEdicionPostal();
  }

  unselectAll(): void {
    this.selectedUserIds.clear();
    this.selectedExternos.clear();
    this.userDomicilios.clear();
    this.editingUserId = null;
    this.editingExternoNif = null;
  }

  selectedUsers(): Usuario[] {
    return Array.from(this.selectedUserIds)
      .map(id => this.selectedUsersCache.get(id))
      .filter((u): u is Usuario => !!u);
  }

  selectedExternosList(): DestinatarioMin[] {
    return Array.from(this.selectedExternos.values());
  }

  destinatariosCount(): number {
    return this.selectedUserIds.size + this.selectedExternos.size;
  }

  removeSelected(id: number): void {
    this.selectedUserIds.delete(id);
    this.userDomicilios.delete(id);
    this.asegurarEdicionPostal();
  }

  removeSelectedExterno(nif: string): void {
    this.selectedExternos.delete(this.normalizaNif(nif) || nif);
    this.asegurarEdicionPostal();
  }

  editarUsuario(id: number): void {
    if (!this.esModoPostal) return;
    this.editingUserId = id;
    this.editingExternoNif = null;
  }

  editarExterno(nif: string): void {
    if (!this.esModoPostal) return;
    this.editingExternoNif = this.normalizaNif(nif) || nif;
    this.editingUserId = null;
  }

  usuarioEnEdicion(id: number): boolean {
    return this.editingUserId === id;
  }

  externoEnEdicion(nif: string): boolean {
    return this.editingExternoNif === (this.normalizaNif(nif) || nif);
  }

  usuarioIncompleto(u: Usuario): boolean {
    return !this.identidadValida(u, false)
      || (this.esModoPostal && !this.domicilioValidoObj(this.userDomicilios.get(u.id)));
  }

  externoIncompleto(e: DestinatarioMin): boolean {
    return !this.identidadValida(e, true)
      || (this.esModoPostal && !this.domicilioValidoObj(e.domicilio));
  }

  editingUsuario(): Usuario | null {
    if (this.editingUserId == null) return null;
    return this.selectedUsersCache.get(this.editingUserId) || null;
  }

  editingExterno(): DestinatarioMin | null {
    if (!this.editingExternoNif) return null;
    return this.selectedExternos.get(this.editingExternoNif) || null;
  }

  /** Mantiene un destinatario en edición; si desaparece, elige el primero incompleto. */
  private asegurarEdicionPostal(): void {
    if (!this.esModoPostal || this.destinatariosCount() === 0) {
      this.editingUserId = null;
      this.editingExternoNif = null;
      return;
    }
    if (this.editingUserId != null && this.selectedUserIds.has(this.editingUserId)) return;
    if (this.editingExternoNif && this.selectedExternos.has(this.editingExternoNif)) return;
    const incompletoUser = this.selectedUsers().find(u => this.usuarioIncompleto(u));
    if (incompletoUser) {
      this.editarUsuario(incompletoUser.id);
      return;
    }
    const incompletoExt = this.selectedExternosList().find(e => this.externoIncompleto(e));
    if (incompletoExt) {
      this.editarExterno(incompletoExt.numero_documento || '');
      return;
    }
    const firstU = this.selectedUsers()[0];
    if (firstU) {
      this.editarUsuario(firstU.id);
      return;
    }
    const firstE = this.selectedExternosList()[0];
    if (firstE) this.editarExterno(firstE.numero_documento || '');
  }

  etiquetaDest(d: DestinatarioMin): string {
    return etiquetaDestinatario(d);
  }

  iniciales(u: DestinatarioMin): string {
    return inicialesDestinatario(u);
  }

  // ---------- Excel import ----------
  triggerExcelInput(): void {
    const input = document.getElementById('excel-import-input') as HTMLInputElement;
    input?.click();
  }

  async onExcelFile(event: Event): Promise<void> {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;

    this.importandoExcel = true;
    this.excelMensaje = '';
    this.excelNoEncontrados = [];
    this.excelIncompletos = [];

    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(new Uint8Array(buffer), { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });

      if (rows.length < 2) {
        this.excelMensaje = this.t('REMESAS.EXCEL.VACIO');
        return;
      }

      const headers = (rows[0] as string[]).map(h => String(h ?? '').toLowerCase().trim());
      const dniCol = this.colIndex(headers, 'dni', 'nif', 'documento');
      const emailCol = this.colIndex(headers, 'email', 'correo', 'mail');
      const nombreCol = this.colIndexNombre(headers);
      const apellidosCol = this.colIndex(headers, 'apellido');
      const telCol = this.colIndex(headers, 'telefono', 'teléfono', 'movil', 'móvil', 'phone');
      const viaCol = this.colIndex(headers, 'tipo_via', 'tipovia');
      const nombreViaCol = this.colIndex(headers, 'nombre_via', 'nombrevia');
      const numeroCol = this.colIndex(headers, 'numero', 'número');
      const cpCol = this.colIndex(headers, 'codigo_postal', 'código postal', 'cp');
      const municipioCol = this.colIndex(headers, 'municipio');
      const provinciaCol = this.colIndex(headers, 'provincia');
      const poblacionCol = this.colIndex(headers, 'poblacion', 'población');
      const paisCol = this.colIndex(headers, 'pais', 'país');
      const linea1Col = this.colIndex(headers, 'linea1', 'línea1', 'linea 1');
      const linea2Col = this.colIndex(headers, 'linea2', 'línea2', 'linea 2');

      if (dniCol === -1 && emailCol === -1) {
        this.excelMensaje = this.t('REMESAS.EXCEL.SIN_COLUMNAS');
        return;
      }

      let found = 0;
      let externos = 0;
      const notFound: string[] = [];
      const incompletos: string[] = [];
      const ovSinExcel: number[] = [];
      const filas = rows.slice(1).filter(row =>
        String(row[dniCol !== -1 ? dniCol : emailCol] ?? '').trim().length > 0
        || (emailCol !== -1 && String(row[emailCol] ?? '').trim().length > 0)
      );

      for (let i = 0; i < filas.length; i += 10) {
        const batch = filas.slice(i, i + 10);
        await Promise.all(batch.map(async row => {
          const nifRaw = dniCol !== -1 ? String(row[dniCol] ?? '').trim() : '';
          const emailRaw = emailCol !== -1 ? String(row[emailCol] ?? '').trim() : '';
          const term = nifRaw || emailRaw;
          const nif = this.normalizaNif(nifRaw);
          try {
            const resp = await firstValueFrom(
              this.usuariosService.list({ page: 0, size: 5, search: term })
            );
            const match = (resp?.data || []).find(u =>
              this.normalizaNif(u.numero_documento) === nif
              || (!!emailRaw && u.email?.toLowerCase() === emailRaw.toLowerCase())
            );
            if (match) {
              if (this.esModoPostal) {
                const dom = this.domicilioDesdeFila(row, {
                  viaCol, nombreViaCol, numeroCol, cpCol, municipioCol, provinciaCol,
                  poblacionCol, paisCol, linea1Col, linea2Col
                });
                if (this.domicilioValidoObj(dom)) {
                  this.userDomicilios.set(match.id, dom);
                } else {
                  ovSinExcel.push(match.id);
                }
              }
              this.selectedUserIds.add(match.id);
              this.selectedUsersCache.set(match.id, match);
              if (nif) this.selectedExternos.delete(nif);
              found++;
              return;
            }
          } catch {
            // se trata abajo como no encontrado / externo
          }

          if (!this.permiteDestinatariosExternos) {
            notFound.push(term);
            return;
          }

          const nombre = nombreCol !== -1 ? String(row[nombreCol] ?? '').trim() : '';
          const apellidos = apellidosCol !== -1 ? String(row[apellidosCol] ?? '').trim() : '';
          const telefono = telCol !== -1 ? String(row[telCol] ?? '').trim() : '';
          if (!nif || !nombre || !apellidos) {
            incompletos.push(term || this.t('REMESAS.DEST.FILA_SIN_NIF'));
            return;
          }
          let domicilio: DomicilioPostal | undefined;
          if (this.esModoPostal) {
            domicilio = this.domicilioDesdeFila(row, {
              viaCol, nombreViaCol, numeroCol, cpCol, municipioCol, provinciaCol,
              poblacionCol, paisCol, linea1Col, linea2Col
            });
            if (!this.domicilioValidoObj(domicilio)) {
              incompletos.push(term || this.t('REMESAS.DEST.SIN_DOMICILIO'));
              return;
            }
          }
          this.selectedExternos.set(nif, {
            numero_documento: nif,
            nombre,
            apellido1: apellidos,
            email: emailRaw,
            telefono,
            externo: true,
            domicilio
          });
          externos++;
        }));
      }

      this.excelNoEncontrados = notFound;
      this.excelIncompletos = incompletos;
      const parts: string[] = [];
      if (found > 0) {
        parts.push(this.t(found === 1 ? 'REMESAS.EXCEL.USUARIO' : 'REMESAS.EXCEL.USUARIOS', { count: found }));
      }
      if (externos > 0) {
        parts.push(this.t(externos === 1 ? 'REMESAS.EXCEL.EXTERNO' : 'REMESAS.EXCEL.EXTERNOS', { count: externos }));
      }
      const imported = parts.length > 0
        ? parts.join(', ') + ' ' + this.t(found + externos !== 1 ? 'REMESAS.EXCEL.IMPORTADOS' : 'REMESAS.EXCEL.IMPORTADO')
        : this.t('REMESAS.EXCEL.NINGUNO');
      const extras: string[] = [];
      if (notFound.length > 0) {
        extras.push(this.t(notFound.length === 1 ? 'REMESAS.EXCEL.NO_ENCONTRADO' : 'REMESAS.EXCEL.NO_ENCONTRADOS', { count: notFound.length }));
      }
      if (incompletos.length > 0) {
        extras.push(this.t(incompletos.length === 1 ? 'REMESAS.EXCEL.INCOMPLETA' : 'REMESAS.EXCEL.INCOMPLETAS', { count: incompletos.length }));
      }
      this.excelMensaje = imported + (extras.length ? ', ' + extras.join(', ') : '') + '.';
      this.prefillDomiciliosUsuarios(ovSinExcel);
      this.asegurarEdicionPostal();

    } catch {
      this.excelMensaje = this.t('REMESAS.EXCEL.ERROR_LECTURA');
    } finally {
      this.importandoExcel = false;
      (event.target as HTMLInputElement).value = '';
    }
  }

  descargarPlantillaExcel(): void {
    const headers: string[] = this.permiteDestinatariosExternos
      ? ['DNI/NIF', 'NOMBRE', 'APELLIDOS', 'EMAIL', 'TELEFONO']
      : ['DNI/NIF', 'EMAIL'];
    const ejemplo: string[] = this.permiteDestinatariosExternos
      ? ['12345678A', 'Nombre', 'Apellidos', 'ejemplo@correo.es', '600000000']
      : ['12345678A', 'ejemplo@correo.es'];
    if (this.esModoPostal) {
      if (this.permitePostalInternacional) {
        headers.push('TIPO_VIA', 'NOMBRE_VIA', 'NUMERO', 'CP', 'MUNICIPIO', 'PROVINCIA', 'POBLACION', 'PAIS', 'LINEA1', 'LINEA2');
        ejemplo.push('CL', 'Mayor', '12', '28001', 'Madrid', '28', 'Madrid', 'ES', '', '');
      } else {
        headers.push('TIPO_VIA', 'NOMBRE_VIA', 'NUMERO', 'CP', 'MUNICIPIO', 'PROVINCIA', 'POBLACION');
        ejemplo.push('CL', 'Mayor', '12', '28001', 'Madrid', '28', 'Madrid');
      }
    }
    const ws = XLSX.utils.aoa_to_sheet([headers, ejemplo]);
    ws['!cols'] = headers.map(h => ({ wch: Math.max(14, h.length + 2) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, this.t('REMESAS.EXCEL.HOJA'));
    XLSX.writeFile(wb, 'plantilla_destinatarios.xlsx');
  }

  private colIndex(headers: string[], ...keys: string[]): number {
    return headers.findIndex(h => keys.some(k => h.includes(k)));
  }

  private normalizaNif(nif?: string | null): string {
    if (!nif) return '';
    return nif.trim().replace(/[.\s-]/g, '').toUpperCase();
  }

  // ---------- Documento ----------
  setDocumentoMode(mode: 'generar' | 'adjuntar' | 'ninguno'): void {
    if (mode === 'generar' && !this.firmaAutomaticaDisponible) return;
    this.documentoMode = mode;
    this.documentoError = '';
    if (mode !== 'adjuntar') {
      this.documentoAdjunto = null;
      this.documentoExistente = null;
    }
    if (!this.puedeFirmarAntesEnviar) this.firmarAntesEnviar = false;
  }

  triggerDocumentoInput(): void {
    const input = document.getElementById('documento-input') as HTMLInputElement;
    input?.click();
  }

  async onDocumentoFile(event: Event): Promise<void> {
    const file = (event.target as HTMLInputElement).files?.[0];
    (event.target as HTMLInputElement).value = '';
    if (!file) return;

    this.documentoError = '';
    if (file.size > this.documentoMaxBytes) {
      this.documentoError = this.t('REMESAS.FORM.DOC_ERROR_TAMANO');
      return;
    }

    try {
      const dataUrl = await this.fileToDataUrl(file);
      // dataUrl => "data:<mime>;base64,<contenido>"; nos quedamos con el base64.
      const contenido = dataUrl.substring(dataUrl.indexOf(',') + 1);
      this.documentoAdjunto = {
        contenido,
        mime: file.type || 'application/octet-stream',
        nombre: file.name
      };
      this.documentoExistente = null;
    } catch {
      this.documentoError = this.t('REMESAS.FORM.DOC_ERROR_LECTURA');
    }
    if (!this.puedeFirmarAntesEnviar) this.firmarAntesEnviar = false;
  }

  quitarDocumento(): void {
    this.documentoAdjunto = null;
    this.documentoExistente = null;
    this.documentoError = '';
  }

  private fileToDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  // ---------- Acciones ----------
  cancelar(): void { this.dialogRef.close(null); }

  guardarBorrador(): void {
    if (!this.step1Valid()) return;
    if (this.destinatariosCount() > 0 && !this.destinatariosCompletos()) return;
    this.dialogRef.close(this.buildResult('draft'));
  }

  enviarAhora(): void {
    if (!this.canFinish()) return;
    this.dialogRef.close(this.buildResult('send'));
  }

  solicitarFirma(): void {
    if (!this.canFinish() || !this.firmanteId) return;
    this.dialogRef.close(this.buildResult('request-sign'));
  }

  programarEnvio(): void {
    if (!this.canFinish()) return;
    if (!this.fechaProgramada) return;
    this.dialogRef.close(this.buildResult('schedule'));
  }

  canFinish(): boolean {
    if (!this.step1Valid() || this.destinatariosCount() === 0) return false;
    return this.destinatariosCompletos();
  }

  private buildResult(action: RemesaFormAction): RemesaFormResult {
    const v = this.form.value;
    const postal = this.esModoPostal;
    const soloApp = this.esModoSoloApp;
    const destinatarios: ComunicacionPayload['destinatarios'] = [
      ...(postal
        ? Array.from(this.selectedUserIds).map(id => ({
            usuarioId: id,
            domicilio: this.userDomicilios.get(id)
          }))
        : Array.from(this.selectedUserIds)),
      ...(soloApp
        ? []
        : this.selectedExternosList().map(e => ({
            nif: e.numero_documento,
            nombre: e.nombre,
            apellidos: [e.apellido1, e.apellido2].filter(Boolean).join(' ').trim() || undefined,
            email: e.email,
            telefono: e.telefono,
            domicilio: postal ? e.domicilio : undefined
          })))
    ];
    const payload: ComunicacionPayload = {
      concepto: v.concepto,
      descripcion: v.descripcion,
      tipoEnvio: Number(v.tipoEnvio),
      destinatarios,
      modoEnvio: this.modoEnvio,
      entregaPostal: postal
    };
    if (v.procedimiento != null && v.procedimiento !== '') {
      payload.procedimiento = Number(v.procedimiento);
    }
    if (this.documentoMode === 'adjuntar' && this.documentoAdjunto) {
      payload.documento = this.documentoAdjunto;
      payload.documentoModo = 'ADJUNTO';
    } else if (this.documentoMode === 'ninguno') {
      payload.documentoModo = 'NINGUNO';
    } else {
      // Modo generar: plantilla elegida (o null = plantilla por defecto).
      payload.plantillaId = this.plantillaId ?? null;
      payload.documentoModo = 'PLANTILLA';
    }
    if (this.data.comunicacion?.id) payload.id = this.data.comunicacion.id;
    payload.firmarAntesEnviar = this.firmarAntesEnviar && this.puedeFirmarAntesEnviar;
    return {
      action,
      payload,
      fechaProgramada: this.fechaProgramada || undefined,
      firmarAntesEnviar: payload.firmarAntesEnviar,
      firmanteId: this.firmanteId || undefined
    };
  }

  private destinatarioToUsuario(d: DestinatarioMin): Usuario {
    return { ...d, id: d.id as number, rol: [], activo: true };
  }

  onEntregaPostalChange(): void {
    this.onModoEnvioChange();
  }

  /** Precarga el domicilio de ficha si el usuario de la app no trae uno válido (Excel/edición). */
  private prefillDomiciliosUsuarios(ids: number[]): void {
    if (!this.esModoPostal || !ids?.length) return;
    const faltan = [...new Set(ids)].filter(id => !this.domicilioValidoObj(this.userDomicilios.get(id)));
    if (!faltan.length) return;
    this.remesasService.domicilios(faltan).subscribe({
      next: (map) => {
        Object.keys(map || {}).forEach(k => {
          const id = Number(k);
          const raw = map[k];
          if (!raw || this.domicilioValidoObj(this.userDomicilios.get(id))) return;
          const d: DomicilioPostal = { ...domicilioPostalVacio(), ...raw };
          if (this.domicilioValidoObj(d)) {
            this.userDomicilios.set(id, d);
          }
        });
      }
    });
  }

  domicilioUsuario(id: number): DomicilioPostal {
    let d = this.userDomicilios.get(id);
    if (!d) {
      d = domicilioPostalVacio();
      this.userDomicilios.set(id, d);
    }
    return d;
  }

  domicilioExterno(e: DestinatarioMin): DomicilioPostal {
    if (!e.domicilio) e.domicilio = domicilioPostalVacio();
    return e.domicilio;
  }

  setTipoDomicilio(d: DomicilioPostal, tipo: number): void {
    d.tipoDomicilio = tipo;
    if (tipo === 1) this.forzarNacional(d);
  }

  domiciliosCompletos(): boolean {
    return this.destinatariosCompletos();
  }

  destinatariosCompletos(): boolean {
    const usersOk = this.selectedUsers().every(u => !this.usuarioIncompleto(u));
    if (this.esModoSoloApp) {
      return usersOk && this.selectedUserIds.size > 0;
    }
    return usersOk && this.selectedExternosList().every(e => !this.externoIncompleto(e));
  }

  domiciliosPendientes(): number {
    return this.destinatariosPendientes();
  }

  destinatariosPendientes(): number {
    return this.selectedUsers().filter(u => this.usuarioIncompleto(u)).length
      + this.selectedExternosList().filter(e => this.externoIncompleto(e)).length;
  }

  domicilioResumen(d?: DomicilioPostal | null): string {
    if (!this.domicilioValidoObj(d)) return '';
    if (d!.tipoDomicilio === 2) {
      return [d!.linea1, d!.codigoPostal, d!.pais].filter(Boolean).join(', ');
    }
    return [d!.tipoVia, d!.nombreVia, d!.numero, d!.codigoPostal, d!.poblacion].filter(Boolean).join(' ');
  }

  plantillaExcelHint(): string {
    const partes: string[] = [];
    if (this.permiteDestinatariosExternos) {
      partes.push(this.t('REMESAS.EXCEL.HINT_CAMPOS_EXTERNOS'));
    } else {
      partes.push(this.t('REMESAS.EXCEL.HINT_CAMPOS_APP'));
    }
    if (this.esModoPostal) {
      partes.push(this.permiteDestinatariosExternos
        ? (this.permitePostalInternacional
          ? this.t('REMESAS.EXCEL.HINT_POSTAL_INT')
          : this.t('REMESAS.EXCEL.HINT_POSTAL_NAC'))
        : this.t('REMESAS.EXCEL.HINT_POSTAL_APP'));
    }
    return this.t('REMESAS.EXCEL.HINT_PREFIJO', { partes: partes.join(' + ') });
  }

  private forzarNacional(d: DomicilioPostal): void {
    d.tipoDomicilio = 1;
    d.pais = 'ES';
    d.linea1 = '';
    d.linea2 = '';
  }

  onCodigoPostalChange(d: DomicilioPostal): void {
    if (!d || d.tipoDomicilio === 2) return;
    const cp = (d.codigoPostal || '').trim();
    if (/^\d{5}$/.test(cp)) {
      d.provincia = cp.substring(0, 2);
    }
  }

  faltaTexto(v?: string | null): boolean {
    return !(v || '').trim();
  }

  faltaCpNacional(d: DomicilioPostal): boolean {
    return !/^\d{5}$/.test((d?.codigoPostal || '').trim());
  }

  faltaProvincia(d: DomicilioPostal): boolean {
    return !/^\d{2}$/.test((d?.provincia || '').trim());
  }

  faltaPaisIso(d: DomicilioPostal): boolean {
    const pais = (d?.pais || '').trim().toUpperCase();
    return pais.length !== 2 || pais === 'ES';
  }

  private identidadValida(d: DestinatarioMin | Usuario, externo: boolean): boolean {
    if (!this.tieneTexto(d?.nombre) || !this.tieneTexto(d?.numero_documento)) return false;
    if (externo) {
      const ap = `${(d as DestinatarioMin).apellido1 || ''} ${(d as DestinatarioMin).apellido2 || ''}`.trim();
      if (!ap) return false;
    }
    return true;
  }

  private tieneTexto(v?: string | null): boolean {
    return !!(v || '').trim();
  }

  private domicilioValidoObj(d?: DomicilioPostal | null): boolean {
    if (!d) return false;
    if (d.tipoDomicilio === 2) {
      if (!this.permitePostalInternacional) return false;
      return !this.faltaPaisIso(d)
        && this.tieneTexto(d.linea1)
        && this.tieneTexto(d.codigoPostal);
    }
    const cp = (d.codigoPostal || '').trim();
    const provincia = (d.provincia || '').trim() || (/^\d{5}$/.test(cp) ? cp.substring(0, 2) : '');
    return this.tieneTexto(d.tipoVia)
      && this.tieneTexto(d.nombreVia)
      && this.tieneTexto(d.numero)
      && /^\d{5}$/.test(cp)
      && this.tieneTexto(d.municipio)
      && /^\d{2}$/.test(provincia)
      && this.tieneTexto(d.poblacion);
  }

  private domicilioDesdeFila(row: any[], cols: {
    viaCol: number; nombreViaCol: number; numeroCol: number; cpCol: number;
    municipioCol: number; provinciaCol: number; poblacionCol: number;
    paisCol: number; linea1Col: number; linea2Col: number;
  }): DomicilioPostal {
    const celda = (i: number) => i !== -1 ? String(row[i] ?? '').trim() : '';
    const pais = celda(cols.paisCol).toUpperCase();
    const linea1 = celda(cols.linea1Col);
    const internacional = this.permitePostalInternacional && !!pais && pais !== 'ES';
    return {
      tipoDomicilio: internacional ? 2 : 1,
      tipoVia: celda(cols.viaCol),
      nombreVia: celda(cols.nombreViaCol),
      numero: celda(cols.numeroCol),
      codigoPostal: celda(cols.cpCol),
      municipio: celda(cols.municipioCol),
      provincia: celda(cols.provinciaCol),
      poblacion: celda(cols.poblacionCol),
      pais: internacional ? pais : (pais || 'ES'),
      linea1,
      linea2: celda(cols.linea2Col)
    };
  }

  private colIndexNombre(headers: string[]): number {
    const exact = headers.findIndex(h => h === 'nombre' || h === 'name');
    if (exact !== -1) return exact;
    return headers.findIndex(h => h.includes('nombre') && !h.includes('via'));
  }
}
